import {
  type ReactNode,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import * as Mtds from '@mattilsynet/design/react'
import type { AnalysisType, Substance, Substances } from '../../types/types'
import { PillTag } from '../pill-tag/PillTag'

const DEFAULT_MAX_VISIBLE_SUBSTANCES = 2
const SUBSTANCE_NAME_SEPARATOR = '\n'

type SubstanceTagsProps = {
  analysisType?: AnalysisType
  substances?: Substances
  emptyValue?: ReactNode
  maxVisible?: number
  showAllWhenFits?: boolean
  showCodes?: boolean
}

type SubstanceTag = {
  key: string
  title: string
  content: ReactNode
}

const formatSubstanceTag = (
  substance: Substance,
  showCodes: boolean
): SubstanceTag | undefined => {
  if (substance.name == null || substance.name.length === 0) return undefined

  const code = substance.code
  const title =
    showCodes && code != null && code.length > 0
      ? `${substance.name} (${code})`
      : substance.name

  if (!showCodes || substance.code == null || substance.code.length === 0) {
    return {
      key: substance.id.toString(),
      title,
      content: substance.name
    }
  }

  return {
    key: substance.id.toString(),
    title,
    content: (
      <span>
        {substance.name} <Mtds.Muted>({substance.code})</Mtds.Muted>
      </span>
    )
  }
}

const getSubstanceTags = (substances?: Substances, showCodes = false) =>
  substances
    ?.map((substance) =>
      substance == null ? undefined : formatSubstanceTag(substance, showCodes)
    )
    .filter(
      (substanceTag): substanceTag is SubstanceTag => substanceTag != null
    )
    .sort((a, b) => a.title.localeCompare(b.title)) ?? []

const getAnalysisTypeSubstances = (analysisType?: AnalysisType): Substances =>
  (analysisType?.substances ?? [])
    .map((analysisTypeSubstance) => analysisTypeSubstance.substance)
    .filter((substance): substance is Substance => substance != null)

export const SubstanceTags = ({
  analysisType,
  substances,
  emptyValue = '-',
  maxVisible = DEFAULT_MAX_VISIBLE_SUBSTANCES,
  showAllWhenFits = true,
  showCodes = false
}: SubstanceTagsProps) => {
  const id = useId().replace(/:/g, '')
  const containerRef = useRef<HTMLDivElement>(null)
  const allSubstancesRef = useRef<HTMLDivElement>(null)
  const collapsedSubstancesRef = useRef<HTMLDivElement>(null)
  const [canShowAllSubstances, setCanShowAllSubstances] = useState(false)
  const substanceTags = useMemo(
    () =>
      getSubstanceTags(
        substances ?? getAnalysisTypeSubstances(analysisType),
        showCodes
      ),
    [analysisType, showCodes, substances]
  )

  const hiddenSubstanceTags = substanceTags.slice(maxVisible)
  const canOverflow = hiddenSubstanceTags.length > 1
  const shouldShowAllSubstances = showAllWhenFits && canShowAllSubstances
  const shouldShowOverflowButton = canOverflow && !shouldShowAllSubstances
  const visibleSubstanceTags = shouldShowOverflowButton
    ? substanceTags.slice(0, maxVisible)
    : substanceTags
  const popoverId = `substance-tags-${id}`
  const substanceTagsKey = substanceTags
    .map((substance) => substance.title)
    .join(SUBSTANCE_NAME_SEPARATOR)

  useLayoutEffect(() => {
    if (!showAllWhenFits) {
      setCanShowAllSubstances(false)
      return
    }

    if (!canOverflow) {
      setCanShowAllSubstances(true)
      return
    }

    const updateCanShowAllSubstances = () => {
      const allSubstancesHeight = allSubstancesRef.current?.scrollHeight
      const collapsedSubstancesHeight =
        collapsedSubstancesRef.current?.scrollHeight

      if (allSubstancesHeight == null || collapsedSubstancesHeight == null) {
        return
      }

      setCanShowAllSubstances(
        allSubstancesHeight <= collapsedSubstancesHeight + 1
      )
    }

    updateCanShowAllSubstances()

    if (typeof ResizeObserver === 'undefined') return

    const resizeObserver = new ResizeObserver(updateCanShowAllSubstances)
    if (containerRef.current != null)
      resizeObserver.observe(containerRef.current)

    return () => resizeObserver.disconnect()
  }, [canOverflow, maxVisible, showAllWhenFits, substanceTagsKey])

  const tags = (items: SubstanceTag[]) => (
    <Mtds.Flex data-align='center' data-gap={1} className='min-w-0 flex-wrap'>
      {items.map((substance) => (
        <PillTag
          key={substance.key}
          title={substance.title}
          className='max-w-full whitespace-normal break-words'
        >
          {substance.content}
        </PillTag>
      ))}
    </Mtds.Flex>
  )

  if (!substanceTags.length) return emptyValue

  return (
    <Mtds.Grid
      ref={containerRef}
      data-gap={1}
      className='relative min-w-0 justify-items-start'
    >
      {tags(visibleSubstanceTags)}
      {shouldShowOverflowButton && (
        <>
          <Mtds.Button
            data-size='sm'
            popoverTarget={popoverId}
            aria-label={`Vis ${hiddenSubstanceTags.length} flere analyser`}
            onClick={(event) => event.stopPropagation()}
          >
            +{hiddenSubstanceTags.length} flere
          </Mtds.Button>
          <Mtds.Popover
            id={popoverId}
            data-overscroll='contain'
            className='max-w-80'
            onClick={(event) => event.stopPropagation()}
          >
            <Mtds.Grid data-gap={2}>
              {hiddenSubstanceTags.map((substance) => (
                <span key={substance.key} className='break-words'>
                  {substance.content}
                </span>
              ))}
            </Mtds.Grid>
          </Mtds.Popover>
        </>
      )}
      {canOverflow && showAllWhenFits && (
        <div
          aria-hidden='true'
          className='invisible absolute inset-x-0 top-0 pointer-events-none'
        >
          <Mtds.Grid ref={allSubstancesRef} data-gap={1}>
            {tags(substanceTags)}
          </Mtds.Grid>
          <Mtds.Grid ref={collapsedSubstancesRef} data-gap={1}>
            {tags(substanceTags.slice(0, maxVisible))}
            <Mtds.Button data-size='sm' tabIndex={-1}>
              +{hiddenSubstanceTags.length} flere
            </Mtds.Button>
          </Mtds.Grid>
        </div>
      )}
    </Mtds.Grid>
  )
}
